import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { SEO } from "@/components/site/SEO";

const field = "w-full rounded-xl border border-warm-300 bg-white px-4 py-3 text-sm focus:border-navy-900 focus:outline-none";

export default function SettingsPage() {
  const { user, api } = usePortalAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/portal/login", { replace: true });
    else if (user && user.role === "installer") navigate("/portal/jobs", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role !== "installer")
      api.get("/settings").then((r) => setS(r.data)).catch(() => setDenied(true));
  }, [user, api]);

  if (user === false || (user && user.role === "installer")) return null;
  if (denied)
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center px-4">
        <div className="rounded-2xl border border-warm-200 bg-white p-10 text-center shadow-sm" data-testid="settings-denied">
          <p className="font-serif text-xl text-slate-900 mb-2">No access</p>
          <p className="text-sm text-slate-500 mb-6">Your role doesn't include portal settings.</p>
          <a href="/portal" className="rounded-full bg-navy-900 px-6 py-2.5 text-xs font-semibold text-white">Back to portal</a>
        </div>
      </div>
    );
  if (!s)
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-navy-900" />
      </div>
    );

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings", {
        deposit_percent: Number(s.deposit_percent),
        followup_days: s.followup_days,
        prefix_quote: s.prefix_quote,
        prefix_order: s.prefix_order,
        prefix_job: s.prefix_job,
        prefix_invoice: s.prefix_invoice,
        terms_text: s.terms_text,
        google_review_url: s.google_review_url,
        hubspot_tracking_id: s.hubspot_tracking_id,
      });
      setS(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-50" data-testid="settings-page">
      <SEO title="Portal Settings | My Blinds Company" description="Portal settings." path="/portal/settings" />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <a href="/portal" className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 mb-6 hover:text-navy-800">
          <ArrowLeft className="h-4 w-4" /> Back to portal
        </a>
        <h1 className="font-serif text-3xl text-slate-900 mb-8">Portal Settings</h1>
        <div className="rounded-2xl border border-warm-200 bg-white p-7 shadow-sm space-y-6">
          <div>
            <label htmlFor="set-deposit" className="text-sm font-medium text-slate-700 block mb-1.5">Deposit required (%)</label>
            <input id="set-deposit" data-testid="set-deposit" type="number" min="0" max="100" value={s.deposit_percent} onChange={(e) => set("deposit_percent", e.target.value)} className={field} />
            <p className="text-xs text-slate-400 mt-1.5">Applied to new quotes — currently {s.deposit_percent}% of the total.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["prefix_quote", "Quote prefix"], ["prefix_order", "Order prefix"], ["prefix_job", "Job prefix"], ["prefix_invoice", "Invoice prefix"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">{label}</label>
                <input value={s[k]} onChange={(e) => set(k, e.target.value)} data-testid={`set-${k}`} className={field} />
              </div>
            ))}
          </div>
          <div>
            <label htmlFor="set-followup" className="text-sm font-medium text-slate-700 block mb-1.5">Quote follow-up days (comma separated)</label>
            <input
              id="set-followup"
              data-testid="set-followup"
              value={(s.followup_days || []).join(", ")}
              onChange={(e) => set("followup_days", e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n)))}
              className={field}
            />
          </div>
          <div>
            <label htmlFor="set-terms" className="text-sm font-medium text-slate-700 block mb-1.5">Quotation terms &amp; conditions</label>
            <textarea id="set-terms" data-testid="set-terms" rows={10} value={s.terms_text} onChange={(e) => set("terms_text", e.target.value)} className={`${field} resize-y leading-relaxed`} />
            <p className="text-xs text-slate-400 mt-1.5">Every quote saves a snapshot of these terms at the moment it's created.</p>
          </div>
          <div className="border-t border-warm-200 pt-6">
            <h2 className="font-serif text-xl text-slate-900 mb-4">Integrations</h2>
            <div className="space-y-5">
              <div>
                <label htmlFor="set-review-url" className="text-sm font-medium text-slate-700 block mb-1.5">Google review link</label>
                <input id="set-review-url" data-testid="set-review-url" value={s.google_review_url || ""} onChange={(e) => set("google_review_url", e.target.value)} placeholder="https://share.google/…" className={field} />
                <p className="text-xs text-slate-400 mt-1.5">Emailed to customers automatically two days after each installation. Find it in your Google Business Profile → "Ask for reviews".</p>
              </div>
              <div>
                <label htmlFor="set-hubspot-tracking" className="text-sm font-medium text-slate-700 block mb-1.5">HubSpot tracking code</label>
                <input id="set-hubspot-tracking" data-testid="set-hubspot-tracking" value={s.hubspot_tracking_id || ""} onChange={(e) => set("hubspot_tracking_id", e.target.value)} placeholder="js-eu1.hs-scripts.com/149399639.js" className={field} />
                <p className="text-xs text-slate-400 mt-1.5">From HubSpot → Settings → Tracking Code. Installed on every public website page. Leave blank to disable.</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="settings-save"
            onClick={save}
            disabled={busy}
            className="w-full rounded-full bg-navy-900 py-3.5 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-40"
          >
            {busy ? "Saving…" : saved ? "Saved ✓" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
